import { UploadFileInfo } from '@progress/kendo-react-upload';

export interface FileWithMetadata {
  file: UploadFileInfo;
  perFileMetadata: Record<string, unknown>;
}

export const isFileExtensionAllowed = (fileName: string, allowedExtensions: string[] | undefined): boolean => {
  if (!allowedExtensions?.length) {
    return true;
  }

  const fileExtension = fileName.split('.').pop()?.toLowerCase();
  if (!fileExtension) {
    return false;
  }

  return allowedExtensions.includes(fileExtension);
};

export const findFileWithDisallowedExtension = (
  files: UploadFileInfo[],
  allowedExtensions: string[] | undefined
): UploadFileInfo | undefined =>
  files.find(file => file?.name && !isFileExtensionAllowed(file.name, allowedExtensions));

interface MaxFilesLimitMessages {
  fileLimitReached: string;
  fileLimitExceeded: (maxFiles: number) => string;
}

export const getMaxFilesLimitErrorMessage = (
  newFilesCount: number,
  currentFilesCount: number,
  maxFiles: number | undefined,
  messages: MaxFilesLimitMessages
): string | null => {
  if (maxFiles == null || newFilesCount <= maxFiles) {
    return null;
  }

  if (currentFilesCount === maxFiles) {
    return messages.fileLimitReached;
  }

  return messages.fileLimitExceeded(maxFiles);
};

export const buildAddedUploadValue = (
  newFiles: UploadFileInfo[],
  multiple: boolean | undefined = false,
  existingValue: unknown
): UploadFileInfo[] | FileWithMetadata[] => {
  if (!multiple) {
    return newFiles;
  }

  const existingFilesWithMetadata = (existingValue as FileWithMetadata[]) || [];
  const existingFileNames = new Set(existingFilesWithMetadata.map(item => item.file?.name));

  const newFilesWithMetadata = newFiles
    .filter(file => !existingFileNames.has(file.name))
    .map(file => ({
      file,
      perFileMetadata: {}
    }));

  return [...existingFilesWithMetadata, ...newFilesWithMetadata];
};

export const triggerFileSelectIfNeeded = (
  onFileSelect: ((files: FileList) => void) | undefined,
  newFiles: UploadFileInfo[]
): void => {
  if (!onFileSelect || newFiles.length === 0) {
    return;
  }

  const rawFiles = newFiles.map(f => f.getRawFile?.()).filter((file): file is File => file != null);

  if (rawFiles.length === 0) {
    return;
  }

  const dataTransfer = new DataTransfer();
  rawFiles.forEach(file => dataTransfer.items.add(file));
  onFileSelect(dataTransfer.files);
};
